<script lang="ts" setup>
import BaseCard from "@/components/BaseCard.vue";
import BaseContent from "@/components/BaseContent.vue";
import BasePill from "@/components/BasePill.vue";
import BaseSubContent from "@/components/BaseSubContent.vue";
import CharacterSkillEffects from "@/components/CharacterSkillEffects.vue";
import CharacterSuperAtkCategory from "@/components/CharacterSuperAtkCategory.vue";
import { usePage } from "@inertiajs/vue3";
import { computed } from "vue";

const page = usePage();

const props = defineProps<{
    superAtks: SuperAtk[];
    superAtkLvMax: number;
    eza?: boolean;
}>();

const superAtksFiltered = computed(() =>
    props.eza
        ? props.superAtks.filter((sa) => sa.lv !== 0)
        : props.superAtks.filter((sa) => sa.lv === 0),
);
</script>

<template>
    <BaseCard class="!gap-0 divide-y divide-green-900/75 !p-0">
        <template #heading
            >Super Attack{{ superAtksFiltered.length === 1 ? "" : "s" }}
        </template>

        <BaseContent
            v-for="superAtk in superAtksFiltered"
            :class="{
                '!to-yellow-300/10': superAtk.ki >= 18,
                '!from-purple-600/10': superAtk.style === 'Unit',
            }"
            class="bg-gradient-to-bl from-transparent to-transparent p-4"
        >
            <template #header
                >{{ superAtk.name }}
                <span
                    v-if="superAtk.style !== 'Normal'"
                    class="inline-flex flex-wrap items-center gap-1"
                >
                    <BasePill
                        v-if="superAtk.ki >= 18"
                        class="h-fit bg-yellow-300/50"
                        >Ultra Super Attack
                    </BasePill>
                    <BasePill
                        v-if="superAtk.style === 'Unit'"
                        class="h-fit bg-purple-600/50"
                        >Unit Super Attack
                    </BasePill>
                </span>
                <CharacterSuperAtkCategory :category="superAtk.category" />
            </template>

            {{ superAtk.description }}

            <BaseSubContent v-if="superAtk.condition" header="condition">
                {{ superAtk.condition }}
            </BaseSubContent>

            <BaseSubContent header="Details">
                <ul class="ml-4 list-disc">
                    <li>
                        Launches from <strong>{{ superAtk.ki }} ki</strong>
                    </li>
                    <li>
                        Increases ATK by
                        <strong>{{ superAtk.atk_multiplier }}%</strong>
                        <BasePill class="ml-1">Super ATK Lv. 1</BasePill>
                        &ndash;
                        <strong
                            >{{
                                superAtk.atk_multiplier +
                                superAtk.atk_multiplier_lv_bonus *
                                    (superAtkLvMax - 1)
                            }}%</strong
                        >
                        <BasePill class="ml-1"
                            >Super ATK Lv. {{ superAtkLvMax }}
                        </BasePill>
                    </li>
                    <li v-if="superAtk.multi_target">
                        Targets <strong>all enemies</strong>
                    </li>
                    <li
                        v-for="secondaryEffect in superAtk.secondary_effects"
                        v-if="superAtk.secondary_effects.length > 0"
                    >
                        <BasePill class="mr-1"
                            >From Super ATK Lv. {{ secondaryEffect.lv }}
                        </BasePill>
                        <strong>{{ secondaryEffect.name }}</strong
                        ><br />
                        {{ secondaryEffect.description }}
                    </li>
                </ul>
            </BaseSubContent>

            <CharacterSkillEffects
                v-if="superAtk.effects.length > 0"
                :effects="superAtk.effects"
            />
        </BaseContent>
    </BaseCard>
</template>

<style scoped></style>
